import type { RouteObject } from 'react-router-dom';
import { Navigate } from 'react-router-dom';

import { ALL_ROUTES } from '../consts/routes';
import {
  api,
  developer,
  deviceBlocks,
  groups,
  Impersonate,
  reports,
  settings,
  tagsManagement,
} from '../pages';
import { createProtectedRoute } from '../utils/createProtectedRoute';
import { VerifyRoute } from '../utils/VerifyRoute';
import { usePrivileges } from '@/hooks/usePrivilleges';
import { MainLayout } from '@/Layouts';

/**
 * Authenticated routes (requires login)
 *
 * @param indexRoute - Default route for authenticated users
 */
export const useAuthenticatedRoutes = (indexRoute: string): RouteObject[] => {
  const { pages } = usePrivileges();

  return [
    {
      path: '/',
      element: <VerifyRoute needAuth userHasPrivileges element={<MainLayout />} />,
      children: [
        // Root redirect
        {
          index: true,
          element: <Navigate to={indexRoute} replace />,
        },

        // Impersonate
        {
          path: ALL_ROUTES.IMPERSONATE,
          element: createProtectedRoute({
            page: Impersonate,
            needAuth: true,
            hasPrivileges: true,
          }),
        },

        // API
        {
          path: ALL_ROUTES.API,
          element: createProtectedRoute({
            page: api.list,
            needAuth: true,
            hasPrivileges: pages.api,
          }),
        },

        // Groups
        {
          path: ALL_ROUTES.GROUPS,
          element: createProtectedRoute({
            page: groups.list,
            needAuth: true,
            hasPrivileges: pages.groups,
          }),
        },

        // Device Blocks
        {
          path: ALL_ROUTES.DEVICE_BLOCKS,
          element: createProtectedRoute({
            page: deviceBlocks.list,
            needAuth: true,
            hasPrivileges: pages.deviceBlocks,
          }),
        },

        // Tags Management
        {
          path: ALL_ROUTES.TAGS_MANAGEMENT,
          element: createProtectedRoute({
            page: tagsManagement.list,
            needAuth: true,
            hasPrivileges: pages.tagsManagement,
          }),
        },

        // Reports
        {
          path: ALL_ROUTES.REPORTS,
          children: [
            {
              index: true,
              element: createProtectedRoute({
                page: reports.list,
                needAuth: true,
                hasPrivileges: pages.reports,
              }),
            },
            {
              path: ALL_ROUTES.REPORTS_CREATE,
              element: createProtectedRoute({
                page: reports.create,
                needAuth: true,
                hasPrivileges: pages.reports,
              }),
            },
          ],
        },

        // Settings
        {
          path: ALL_ROUTES.SETTINGS,
          children: [
            {
              index: true,
              element: (
                <VerifyRoute
                  needAuth
                  userHasPrivileges={pages.settings.overview}
                  element={<Navigate to='overview' replace />}
                />
              ),
            },
            {
              path: ALL_ROUTES.SETTINGS_OVERVIEW,
              element: createProtectedRoute({
                page: settings.overview,
                needAuth: true,
                hasPrivileges: pages.settings.overview,
              }),
            },
            {
              path: ALL_ROUTES.SETTINGS_MY_ACCOUNT,
              element: createProtectedRoute({
                page: settings.myAccount,
                needAuth: true,
                hasPrivileges: pages.settings.myAccount,
              }),
            },
            {
              path: ALL_ROUTES.SETTINGS_LOGIN_SECURITY,
              element: createProtectedRoute({
                page: settings.loginSecurity,
                needAuth: true,
                hasPrivileges: pages.settings.loginSecurity,
              }),
            },
            {
              path: ALL_ROUTES.SETTINGS_USERS,
              element: createProtectedRoute({
                page: settings.users,
                needAuth: true,
                hasPrivileges: pages.settings.users,
              }),
            },
            {
              path: ALL_ROUTES.SETTINGS_AUDIT,
              element: createProtectedRoute({
                page: settings.audit,
                needAuth: true,
                hasPrivileges: pages.settings.audit,
              }),
            },
          ],
        },

        // Developer
        {
          path: ALL_ROUTES.DEVELOPER,
          children: [
            {
              index: true,
              element: <Navigate to={ALL_ROUTES.DEVELOPER_SDK} replace />,
            },
            {
              path: ALL_ROUTES.DEVELOPER_SDK,
              element: createProtectedRoute({
                page: developer.sdk,
                needAuth: true,
                hasPrivileges: pages.developer.sdk,
              }),
            },
            {
              path: ALL_ROUTES.DEVELOPER_BATCH,
              children: [
                {
                  index: true,
                  element: createProtectedRoute({
                    page: developer.batch,
                    needAuth: true,
                    hasPrivileges: pages.developer.batch,
                  }),
                },
                {
                  path: ALL_ROUTES.DEVELOPER_BATCH_CREATE,
                  element: createProtectedRoute({
                    page: developer.batchCreate,
                    needAuth: true,
                    hasPrivileges: pages.developer.batch,
                  }),
                },
              ],
            },
            {
              path: ALL_ROUTES.DEVELOPER_CREDENTIALS,
              children: [
                {
                  index: true,
                  element: createProtectedRoute({
                    page: developer.credentials,
                    needAuth: true,
                    hasPrivileges: pages.developer.credentials,
                  }),
                },
                {
                  path: ALL_ROUTES.DEVELOPER_CREDENTIALS_CREATE,
                  element: createProtectedRoute({
                    page: developer.credentialsCreate,
                    needAuth: true,
                    hasPrivileges: pages.developer.credentials,
                  }),
                },
              ],
            },
          ],
        },
      ],
    },
  ];
};
